
import { supabase } from '@/integrations/supabase/client';
import { SurveyResponse, ScoreBreakdown } from '@/types/survey';

// Save survey response to Supabase
export const saveSurveyResponse = async (
  projectName: string,
  industry: string,
  responses: SurveyResponse,
  scoreBreakdown: ScoreBreakdown
) => {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('survey_responses')
    .insert({
      user_id: userData.user.id,
      project_name: projectName,
      industry: industry,
      responses: responses as any,
      score_breakdown: scoreBreakdown as any
    })
    .select()
    .single();

  if (error) {
    console.error('Error saving survey response:', error);
    throw error;
  }

  return data;
};

// Save survey response to localStorage for non-authenticated users or offline usage
export const saveSurveyLocal = (
  projectName: string,
  industry: string,
  responses: SurveyResponse
) => {
  try {
    // Generate a unique ID for the survey response
    const id = `survey_${Date.now()}`;
    
    const surveyData = {
      id,
      project_name: projectName,
      industry,
      responses,
      created_at: new Date().toISOString()
    };
    
    // Get existing surveys or initialize empty array
    const existingSurveys = JSON.parse(localStorage.getItem('nev-saved-surveys') || '[]');
    
    // Add new survey data
    existingSurveys.push(surveyData);
    
    // Save back to localStorage
    localStorage.setItem('nev-saved-surveys', JSON.stringify(existingSurveys));
    
    return surveyData;
  } catch (error) {
    console.error('Error saving survey to localStorage:', error);
    throw error;
  }
};

// Get locally stored surveys
export const getSurveyLocal = () => {
  try {
    const surveys = JSON.parse(localStorage.getItem('nev-saved-surveys') || '[]');
    return surveys;
  } catch (error) {
    console.error('Error getting surveys from localStorage:', error);
    return [];
  }
};

// Get user's saved survey responses
export const getUserSurveyResponses = async () => {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  console.log('Checking for duplicate entries in survey_responses');
  
  const { data, error } = await supabase
    .from('survey_responses')
    .select('*')
    .eq('user_id', userData.user.id as any)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error getting survey responses:', error);
    throw error;
  }

  // Handle deduplication in the client
  const uniqueProjectMap = new Map();
  
  // Keep only the most recent entry for each project name
  const uniqueData = data.filter(item => {
    if (uniqueProjectMap.has(item.project_name)) {
      const existingItem = uniqueProjectMap.get(item.project_name);
      // If current item is newer, replace and keep it
      if (new Date(item.created_at) > new Date(existingItem.created_at)) {
        uniqueProjectMap.set(item.project_name, item);
        return true;
      }
      return false; // Skip this item as we already have a newer version
    }
    
    // First time seeing this project name, keep it
    uniqueProjectMap.set(item.project_name, item);
    return true;
  });

  return uniqueData;
};

// Get a specific survey response by ID
export const getSurveyResponseById = async (id: string) => {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { data, error } = await supabase
    .from('survey_responses')
    .select('*')
    .eq('id', id as any)
    .eq('user_id', userData.user.id as any)
    .single();

  if (error) {
    console.error('Error getting survey response:', error);
    throw error;
  }

  return data;
};

// Delete a survey response
export const deleteSurveyResponse = async (id: string) => {
  const { data: userData } = await supabase.auth.getUser();
  
  if (!userData.user) {
    throw new Error('User not authenticated');
  }

  const { error } = await supabase
    .from('survey_responses')
    .delete()
    .eq('id', id as any)
    .eq('user_id', userData.user.id as any);

  if (error) {
    console.error('Error deleting survey response:', error);
    throw error;
  }

  return true;
};
